import React from 'react'
import {
    Card,
    CardContent,
    Grid,
    Typography,
    Chip,
    Box,
    LinearProgress,
    IconButton,
    Collapse
} from '@material-ui/core'
import {
    CheckCircle,
    ExpandMore as ExpandMoreIcon,
    Room as LocationIcon
} from '@material-ui/icons'
import { makeStyles } from '@material-ui/core/styles'
import WcIcon from '@mui/icons-material/Wc';

const useStyles = makeStyles((theme) => ({
    scoreCard: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        transition: 'box-shadow 0.3s',
        '&:hover': {
            boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
        }
    },
    scoreChip: {
        fontWeight: 600,
        fontSize: '14px'
    },
    highScore: {
        backgroundColor: '#4caf50',
        color: 'white'
    },
    mediumScore: {
        backgroundColor: '#ff9800',
        color: 'white'
    },
    lowScore: {
        backgroundColor: '#f44336',
        color: 'white'
    },
    breakdownItem: {
        marginBottom: theme.spacing(0.5)
    },
    progressBar: {
        height: 8,
        borderRadius: 4,
        marginTop: theme.spacing(0.5)
    },
    reasonItem: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: theme.spacing(0.5)
    },
    expandButton: {
        transform: 'rotate(0deg)',
        transition: theme.transitions.create('transform', {
            duration: theme.transitions.duration.shortest,
        })
    },
    expandButtonOpen: {
        transform: 'rotate(180deg)'
    }
}))

/**
 * Component to display recommendation score and breakdown
 * - Compact: used in table view (minimal layout)
 * - Full: used in detailed view (with card, breakdown, etc.)
 */
const RecommendationScoreCard = ({ candidate, compact = false, mutualStats }) => {
    const classes = useStyles()
    const [expanded, setExpanded] = React.useState(false)

    if (!candidate) return null

    const {
        recommendationScore = 0,
        scoreBreakdown = {},
        recommendationReasons = [],
        distance,
        age
    } = candidate

    const getScoreClass = (score) => {
        if (score >= 70) return classes.highScore
        if (score >= 50) return classes.mediumScore
        return classes.lowScore
    }

    const getProgressColor = (score, max) => {
        const percentage = (score / max) * 100
        if (percentage >= 70) return 'primary'
        if (percentage >= 50) return 'secondary'
        return 'inherit'
    }

    const BreakdownItem = ({ label, score, maxScore }) => (
        <Grid item xs={12} className={classes.breakdownItem}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" style={{ fontSize: 12 }}>
                    {label}
                </Typography>
                <Typography variant="body2" style={{ fontSize: 12, fontWeight: 600 }}>
                    {score}/{maxScore}
                </Typography>
            </Box>
            <LinearProgress
                variant="determinate"
                value={(score / maxScore) * 100}
                color={getProgressColor(score, maxScore)}
                className={classes.progressBar}
            />
        </Grid>
    )

    /** COMPACT VIEW: for table cell */
    if (compact) {
        const matchLabel =
            recommendationScore >= 70
                ? 'Excellent Match'
                : recommendationScore >= 50
                ? 'Good Match'
                : 'Low Match'

        return (
            <Box display="flex" flexDirection="column" width="100%">
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" style={{ fontWeight: 600 }}>
                        {matchLabel}
                    </Typography>
                    <Chip
                        label={`${recommendationScore}/100`}
                        className={`${classes.scoreChip} ${getScoreClass(recommendationScore)}`}
                        size="small"
                    />
                </Box>
                <Typography
                    variant="caption"
                    style={{ color: '#666', marginTop: 2 }}
                >
                    {recommendationReasons?.length
                        ? recommendationReasons.slice(0, 2).join(', ')
                        : 'No specific reasons'}
                </Typography>
            </Box>
        )
    }

    /** FULL VIEW: with card, details, breakdown, etc. */
    return (
        <Card className={classes.scoreCard}>
            <CardContent>
                <Grid container spacing={1}>
                    {/* Score Header */}
                    <Grid item xs={12}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                                Match Score
                            </Typography>
                            <Chip
                                label={`${recommendationScore}/100`}
                                className={`${classes.scoreChip} ${getScoreClass(recommendationScore)}`}
                                size="small"
                            />
                        </Box>
                    </Grid>

                    {/* Quick Stats */}
                    <Grid item xs={12}>
                        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                            {age && (
                                <Chip
                                    label={`Age: ${age}`}
                                    size="small"
                                    variant="outlined"
                                />
                            )}
                            {distance !== null && distance !== undefined && (
                                <Chip
                                    icon={<LocationIcon style={{ fontSize: 14 }} />}
                                    label={`${distance} miles`}
                                    size="small"
                                    variant="outlined"
                                />
                            )}
                                <Chip
                                    icon={<WcIcon style={{ fontSize: 14 }} />}
                                    label={candidate.isMutualMatch ? "Mutual" : "One-Way"}
                                    size="small"
                                    variant="outlined"
                                />
                        </Box>
                    </Grid>

                    {/* Top Reasons */}
                    <Grid item xs={12}>
                        <Typography
                            variant="subtitle2"
                            style={{ fontWeight: 600, marginTop: 8, marginBottom: 4 }}
                        >
                            Why This Match:
                        </Typography>
                        {recommendationReasons.length > 0 ? (
                            recommendationReasons.map((reason, idx) => (
                                <Box key={idx} className={classes.reasonItem}>
                                    <CheckCircle
                                        style={{
                                            fontSize: 16,
                                            color: '#4caf50',
                                            marginRight: 8
                                        }}
                                    />
                                    <Typography variant="body2" style={{ fontSize: 12 }}>
                                        {reason}
                                    </Typography>
                                </Box>
                            ))
                        ) : (
                            <Typography variant="body2" style={{ fontSize: 12, color: '#999' }}>
                                No specific reasons available
                            </Typography>
                        )}
                    </Grid>

                    {/* Expandable Breakdown */}
                    <Grid item xs={12}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography
                                variant="subtitle2"
                                style={{ fontWeight: 600, fontSize: 12 }}
                            >
                                Detailed Breakdown
                            </Typography>
                            <IconButton
                                size="small"
                                onClick={() => setExpanded(!expanded)}
                                className={`${classes.expandButton} ${expanded ? classes.expandButtonOpen : ''}`}
                            >
                                <ExpandMoreIcon />
                            </IconButton>
                        </Box>

                        <Collapse in={expanded} timeout="auto" unmountOnExit>
                            <Grid container spacing={1} style={{ marginTop: 4 }}>
                                <BreakdownItem label="Location Compatibility" score={scoreBreakdown.location || 0} maxScore={10} />
                                <BreakdownItem label="Age Compatibility" score={scoreBreakdown.age || 0} maxScore={10} />
                                <BreakdownItem label="Gender Match" score={scoreBreakdown.gender || 0} maxScore={10} />
                                <BreakdownItem label="Attachment Style" score={scoreBreakdown.attachment || 0} maxScore={10} />
                                <BreakdownItem label="Shared Values" score={scoreBreakdown.values || 0} maxScore={15} />
                                <BreakdownItem label="Lifestyle Match" score={scoreBreakdown.lifestyle || 0} maxScore={20} />
                                <BreakdownItem label="Family Plans" score={scoreBreakdown.familyPlans || 0} maxScore={10} />
                                <BreakdownItem label="Education Level" score={scoreBreakdown.education || 0} maxScore={10} />
                                <BreakdownItem label="Shared Interests" score={scoreBreakdown.interests || 0} maxScore={10} />
                            </Grid>
                        </Collapse>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    )
}

export default RecommendationScoreCard
